/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useLocation } from "react-router";
import { useEffect, RefObject } from "react";

type UseHashScrollProps = { 
  offsetY?: number; 
  offsetX?: number; 
  scrollContainer?: RefObject<HTMLElement> | null;
};
type UseHashScroll = (props?: UseHashScrollProps) => void;

/**
 * scrollContainer function.
 *
 * @param {HTMLElement} container - The container to scroll
 * @param {string} elementId - The ID of the element to scroll to
 * @param {number} offsetY - The vertical offset to apply
 */
function scrollContainer(container: HTMLElement, elementId: string, offsetY: number): void {
  const element = document.getElementById(elementId);
  if (!element) {
    return;
  }
  
  // Calculate the element's position relative to the container
  const currentScrollTop = container.scrollTop;
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const elementTopRelativeToContainer = elementRect.top - containerRect.top + currentScrollTop;
  
  // Apply offset (offsetY is negative, so we add it)
  const targetScrollTop = elementTopRelativeToContainer + offsetY;
  
  container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
}

/**
 * scrollWindow function.
 *
 * @param {HTMLElement} element - The element to scroll to
 * @param {number} offsetX - The horizontal offset
 * @param {number} offsetY - The vertical offset
 */
function scrollWindow(element: HTMLElement, offsetX: number, offsetY: number): void {
  window?.scrollTo({
    top: element.offsetTop + offsetY,
    left: element.offsetLeft + offsetX,
    behavior: "smooth",
  });
}

/**
 * Checks if container is ready for scrolling.
 *
 * @param {HTMLElement | null | undefined} container - The container to check
 * @returns {boolean} True if ready
 */
function isContainerReady(container: HTMLElement | null | undefined): boolean {
  return container !== null && container !== undefined && container.scrollHeight > 0;
}

/**
 * Attempts scroll with container.
 *
 * @param {HTMLElement} element - The element to scroll to
 * @param {HTMLElement} container - The container to scroll
 * @param {string} elementId - The ID of the element
 * @param {number} offsetY - The vertical offset
 * @returns {boolean} Always true
 */
function attemptScrollWithContainer(
  element: HTMLElement,
  container: HTMLElement,
  elementId: string,
  offsetY: number
): boolean {
  scrollContainer(container, elementId, offsetY);
  return true;
}

/**
 * Attempts scroll with window.
 *
 * @param {HTMLElement} element - The element to scroll to
 * @param {number} offsetX - The horizontal offset
 * @param {number} offsetY - The vertical offset
 * @returns {boolean} Always true
 */
function attemptScrollWithWindow(
  element: HTMLElement,
  offsetX: number,
  offsetY: number
): boolean {
  scrollWindow(element, offsetX, offsetY);
  return true;
}

/**
 * Attempts scroll.
 *
 * @param {string} elementId - The ID of the element to scroll to
 * @param {RefObject<HTMLElement> | null | undefined} scrollContainer - The scroll container ref
 * @param {number} offsetX - The horizontal offset
 * @param {number} offsetY - The vertical offset
 * @returns {boolean} True if scroll was attempted
 */
function attemptScroll(
  elementId: string,
  scrollContainer: RefObject<HTMLElement> | null | undefined,
  offsetX: number,
  offsetY: number
): boolean {
  const element = document.getElementById(elementId);
  if (!element) {
    return false;
  }

  const container = scrollContainer?.current;
  
  if (isContainerReady(container)) {
    return attemptScrollWithContainer(element, container!, elementId, offsetY);
  }
  
  if (!container) {
    return attemptScrollWithWindow(element, offsetX, offsetY);
  }
  
  return false;
}

export const useHashScroll: UseHashScroll = (props) => {
  const { offsetX = 0, offsetY = 0, scrollContainer } = props ?? {};
  const location = useLocation();
  const hash = location.hash;

  useEffect(() => {
    if (!hash) return;
    
    const elementId = hash.replaceAll("#", "");
    
    // Try immediately first
    if (attemptScroll(elementId, scrollContainer, offsetX, offsetY)) {
      return;
    }
    
    // If not ready, try with increasing delays
    const delays = [100, 300, 500, 1000];
    const timeoutIds: NodeJS.Timeout[] = [];
    
    delays.forEach((delay) => {
      const timeoutId = setTimeout(() => {
        if (attemptScroll(elementId, scrollContainer, offsetX, offsetY)) {
          // Clear remaining timeouts
          timeoutIds.forEach(id => clearTimeout(id));
        }
      }, delay);
      timeoutIds.push(timeoutId);
    });
    
    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [hash, offsetX, offsetY, scrollContainer]);
};

/**
 * getScrollParent component.
 *
 * @param {*} node - Parameter description
 *
 * @returns {JSX.Element} React component element
 */
export function getScrollParent(node: HTMLElement) {
  if (node === null) return null;

  if (node.scrollHeight > node.clientHeight) {
    return node;
  } else {
    return getScrollParent(node.parentNode as HTMLElement);
  }
}
